import React, { useState } from 'react';
import { CreditCard, AlertTriangle } from 'lucide-react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '../../../utils/stripe';

const stripePromise = getStripe();

const STATUS_TEXT = {
    ready: null,
    missing: 'No payment method on file',
    expired: 'Card has expired',
    expiring_soon: 'Card expires soon',
    unavailable: 'Payment method unavailable',
};

function formatExpiry(month, year) {
    if (!month || !year) return null;
    const m = String(month).padStart(2, '0');
    const y = String(year).slice(-2);
    return `${m}/${y}`;
}

function capitalizeFirst(str) {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// Dark-theme styling for the Stripe-hosted card input.
const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: '#F8FAFC',
            fontSize: '15px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            '::placeholder': { color: '#64748B' },
        },
        invalid: { color: '#FCA5A5', iconColor: '#FCA5A5' },
    },
};

/**
 * Inline card entry. Creates a SetupIntent, confirms it with Stripe (which
 * validates the card, including any 3-D Secure step), and hands the resulting
 * payment method id back to the parent to set as default.
 */
function CardForm({ createSetupIntent, onConfirmed, onCancel, submitLabel }) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    async function handleSubmit(event) {
        event.preventDefault();
        if (!stripe || !elements || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            const { client_secret } = await createSetupIntent();
            if (!client_secret) throw new Error('Could not start card setup');
            const result = await stripe.confirmCardSetup(client_secret, {
                payment_method: { card: elements.getElement(CardElement) },
            });
            if (result.error) {
                setError(result.error.message || 'That card could not be validated');
                return;
            }
            const pmId = result.setupIntent?.payment_method;
            if (!pmId) {
                setError('Card setup did not complete');
                return;
            }
            await onConfirmed(pmId);
        } catch (err) {
            setError(err.message || 'That card could not be saved');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <form onSubmit={handleSubmit}>
            <div style={styles.cardElementWrap}>
                <CardElement options={CARD_ELEMENT_OPTIONS} />
            </div>
            {error && (
                <div style={styles.formError}>
                    <AlertTriangle size={12} style={{ marginRight: 4 }} />
                    {error}
                </div>
            )}
            <div style={styles.formActions}>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={submitting}
                    style={styles.secondaryButton}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={!stripe || submitting}
                    style={styles.primaryButton}
                >
                    {submitting ? 'Saving…' : (submitLabel || 'Save card')}
                </button>
            </div>
        </form>
    );
}

export function PaymentMethodCard({
    paymentMethod,
    hasStripeCustomer,
    onAddViaCheckout,
    createSetupIntent,
    onCardConfirmed,
    onDeleteCard,
    onManageBilling,
    loading,
}) {
    const [editing, setEditing] = useState(false);
    const hasCard = paymentMethod && paymentMethod.status !== 'missing' && paymentMethod.last4;
    const status = paymentMethod?.status || 'missing';
    const statusText = STATUS_TEXT[status];
    const isWarning = status === 'expired' || status === 'expiring_soon';

    async function handleConfirmed(paymentMethodId) {
        await onCardConfirmed(paymentMethodId);
        setEditing(false);
    }

    // In-app editing is only possible once a Stripe customer exists and Stripe.js
    // is configured. Otherwise the path to a card is Checkout (plan + card).
    const canEditInApp = hasStripeCustomer && !!stripePromise;

    function handleAddOrReplace() {
        if (canEditInApp) {
            setEditing(true);
        } else if (hasStripeCustomer) {
            // Fallback: we have a customer but Stripe.js is unavailable.
            // Redirect to the hosted Customer Portal.
            onManageBilling();
        }
    }

    return (
        <section style={styles.section}>
            <h3 style={styles.sectionTitle}>Payment method</h3>

            <div style={styles.card}>
                {editing && canEditInApp ? (
                    <Elements stripe={stripePromise}>
                        <CardForm
                            createSetupIntent={createSetupIntent}
                            onConfirmed={handleConfirmed}
                            onCancel={() => setEditing(false)}
                            submitLabel={hasCard ? 'Replace card' : 'Save card'}
                        />
                    </Elements>
                ) : (
                    <>
                        <div style={styles.cardContent}>
                            <div style={styles.iconWrapper}>
                                <CreditCard size={24} style={{ color: '#94A3B8' }} />
                            </div>
                            <div style={styles.details}>
                                {hasCard ? (
                                    <>
                                        <div style={styles.cardInfo}>
                                            {capitalizeFirst(paymentMethod.brand)} ending in {paymentMethod.last4}
                                        </div>
                                        <div style={styles.cardExpiry}>
                                            Expires {formatExpiry(paymentMethod.exp_month, paymentMethod.exp_year)}
                                        </div>
                                    </>
                                ) : (
                                    <div style={styles.noCard}>
                                        {statusText || 'No payment method added'}
                                    </div>
                                )}
                                {isWarning && statusText && (
                                    <div style={styles.warning}>
                                        <AlertTriangle size={12} style={{ marginRight: 4 }} />
                                        {statusText}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={styles.actions}>
                            {!hasStripeCustomer ? (
                                // No Stripe customer yet: the way to add a card is to
                                // choose a plan and enter it at Checkout.
                                <button
                                    type="button"
                                    onClick={onAddViaCheckout}
                                    disabled={loading}
                                    style={styles.primaryButton}
                                >
                                    Add payment method
                                </button>
                            ) : hasCard ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleAddOrReplace}
                                        disabled={loading}
                                        style={styles.secondaryButton}
                                    >
                                        Replace card
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onDeleteCard}
                                        disabled={loading}
                                        style={styles.dangerButton}
                                    >
                                        Delete
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleAddOrReplace}
                                    disabled={loading}
                                    style={styles.primaryButton}
                                >
                                    Add payment method
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}

const styles = {
    section: {
        marginBottom: 24,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: '#CBD5E1',
        margin: '0 0 12px',
    },
    card: {
        background: '#0F172A',
        border: '1px solid rgba(148, 163, 184, 0.18)',
        borderRadius: 8,
        padding: 16,
    },
    cardContent: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 16,
    },
    iconWrapper: {
        width: 48,
        height: 48,
        borderRadius: 8,
        background: 'rgba(148, 163, 184, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    details: {
        flex: 1,
    },
    cardInfo: {
        fontSize: 15,
        fontWeight: 600,
        color: '#F8FAFC',
        marginBottom: 4,
    },
    cardExpiry: {
        fontSize: 13,
        color: '#C3CDDC',
    },
    noCard: {
        fontSize: 14,
        color: '#C3CDDC',
    },
    warning: {
        display: 'flex',
        alignItems: 'center',
        fontSize: 12,
        color: '#FCD34D',
        marginTop: 8,
    },
    actions: {
        display: 'flex',
        gap: 12,
    },
    cardElementWrap: {
        padding: '12px 14px',
        background: 'rgba(148, 163, 184, 0.06)',
        border: '1px solid rgba(148, 163, 184, 0.25)',
        borderRadius: 6,
        marginBottom: 12,
    },
    formError: {
        display: 'flex',
        alignItems: 'center',
        fontSize: 12,
        color: '#FCA5A5',
        marginBottom: 12,
    },
    formActions: {
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end',
    },
    primaryButton: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '10px 16px',
        border: '1px solid rgba(96, 165, 250, 0.4)',
        borderRadius: 6,
        background: '#2563EB',
        color: '#FFFFFF',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
    },
    secondaryButton: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '10px 16px',
        border: '1px solid rgba(148, 163, 184, 0.25)',
        borderRadius: 6,
        background: 'transparent',
        color: '#94A3B8',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
    },
    dangerButton: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '10px 16px',
        border: '1px solid rgba(239, 68, 68, 0.35)',
        borderRadius: 6,
        background: 'transparent',
        color: '#FCA5A5',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
    },
};
